import * as THREE from 'three';
import * as TWEEN from '@tweenjs/tween.js';

import { ClipTask, ClipMethod, CameraMode, ElevationGradientRepeat } from '../../shared/defines.js';
import { Renderer } from '../point-cloud-renderer.js';
import { PotreeRenderer } from './potree-renderer.js';
import { EDLRenderer } from './edl-renderer.js';
import { Scene } from './scene.js';
import { ClippingTool } from '../../viewer-utils/tools/clipping-tool.js';
import { TransformationTool } from '../../viewer-utils/tools/transformation-tool.js';
import { Utils } from '../../shared/utils.js';
import { BoxVolume } from '../../viewer-utils/tools/volume.js';
import { getFeatures } from '../../services/features.service';

import { AnnotationTool } from '../tools/annotation-tool.js';
import { MeasuringTool } from '../tools/measuring-tool.js';
import { ProfileTool } from '../tools/profile-tool.js';
import { VolumeTool } from '../../viewer-utils/tools/volume-tool.js';

import { InputHandler } from '../../viewer-utils/navigation/input-handler.js';
import { NavigationCube } from '../../viewer-utils/navigation-cube.js';
import { OrbitControls } from '../../viewer-utils/navigation/orbit-controls.js';
import { FirstPersonControls } from '../../viewer-utils/navigation/first-person-controls.js';
import { EventDispatcher } from '../../viewer-utils/event-dispatcher.js';
import { ClassificationScheme, ClassificationEntry } from '../materials/classification-scheme.js';
import { updatePointClouds } from '../update-visibility.js';
import { incrementFrameNumber } from '../../services/config.service.js';
import { TilesManager } from '../../viewer-utils/terrain/tiles-manager.js';
import { QuantizedMeshTerrainManager } from '../../viewer-utils/terrain/quantized-mesh-terrain-manager.js';
import { tweenGroup } from '../../viewer-utils/tween-group.js';
import { PointCloudOctree } from '../point-cloud-octree.js';
import type { ClipBoxDescriptor } from '../materials/point-cloud-material.js';

// Side-effect imports: monkey-patch THREE.js prototypes
import '../../viewer-utils/extensions/orthographic-camera.js';
import '../../viewer-utils/extensions/perspective-camera.js';
import '../../viewer-utils/extensions/ray.js';

// Re-export tweenGroup for backwards compatibility
export { tweenGroup } from '../../viewer-utils/tween-group.js';

type Skybox = ReturnType<typeof Utils.loadSkybox>;
type Controls = OrbitControls | FirstPersonControls;

interface VisibilityConfig {
  pointBudget: number;
  maxNodesLoading: number;
  transformVersions: Map<PointCloudOctree, { number: number; transform: THREE.Matrix4 }>;
}

/** Duck-typed shape accepted by the `zoomTo`/camera `zoomTo` extension - see viewer-utils/extensions. */
interface ZoomableNode extends THREE.Object3D {
  boundingSphere?: THREE.Sphere;
  boundingBox?: THREE.Box3;
  geometry?: THREE.BufferGeometry & { boundingSphere?: THREE.Sphere | null };
}

export class Viewer extends EventDispatcher {
  renderArea: HTMLElement;
  server!: string | null;
  fov!: number;
  isFlipYZ!: boolean;
  useDEMCollisions!: boolean;
  generateDEM!: boolean;
  minNodeSize!: number;
  edlStrength!: number;
  edlRadius!: number;
  edlOpacity!: number;
  useEDL!: boolean;
  description!: string;
  classifications!: Record<number | 'DEFAULT', ClassificationEntry>;
  moveSpeed!: number;
  baseMoveSpeed!: number;
  showBoundingBox!: boolean;
  freeze!: boolean;
  clipTask!: ClipTask;
  clipMethod!: ClipMethod;
  elevationGradientRepeat!: ElevationGradientRepeat;
  filterReturnNumberRange!: number[];
  filterNumberOfReturnsRange!: number[];
  filterGPSTimeRange!: number[];
  filterPointSourceIDRange!: number[];
  defaultRenderer!: PotreeRenderer | null;
  edlRenderer!: EDLRenderer;
  renderer!: THREE.WebGLRenderer;
  pRenderer!: Renderer;
  scene!: Scene;
  overlay!: THREE.Scene | null;
  overlayCamera!: THREE.OrthographicCamera | null;
  inputHandler!: InputHandler;
  controls!: Controls | null;
  clippingTool!: ClippingTool;
  transformationTool!: TransformationTool;
  navigationCube!: NavigationCube;
  tilesManager!: TilesManager | null;
  quantizedTerrainManager!: QuantizedMeshTerrainManager | null;
  skybox!: Skybox | null;
  clock: THREE.Clock;
  background!: string | null;
  shadowTestCam!: THREE.PerspectiveCamera;
  fpControls!: FirstPersonControls;
  orbitControls!: OrbitControls;
  scaleFactor!: number;
  annotationTool!: AnnotationTool;
  measuringTool!: MeasuringTool;
  profileTool!: ProfileTool;
  volumeTool!: VolumeTool;
  _customVisibilityConfig!: VisibilityConfig | null;
  _visibilityConfig?: Map<PointCloudOctree, { number: number; transform: THREE.Matrix4 }>;
  _previousCamera?: THREE.Camera;

  constructor(domElement: HTMLElement, _args: Record<string, unknown> = {}) {
    super();

    this.renderArea = domElement;
    this.clock = new THREE.Clock();
    this.scaleFactor = 1;

    try {
      this.server = null;

      this.fov = 60;
      this.isFlipYZ = false;
      this.useDEMCollisions = false;
      this.generateDEM = false;
      this.minNodeSize = 30;
      this.edlStrength = 1.0;
      this.edlRadius = 1.4;
      this.edlOpacity = 1.0;
      this.useEDL = false;
      this.description = '';

      this.classifications = ClassificationScheme.DEFAULT;

      this.moveSpeed = 10;
      this.baseMoveSpeed = 10;

      this.showBoundingBox = false;
      this.freeze = false;
      this.clipTask = ClipTask.HIGHLIGHT;
      this.clipMethod = ClipMethod.INSIDE_ANY;

      this.elevationGradientRepeat = ElevationGradientRepeat.CLAMP;

      this.filterReturnNumberRange = [0, 7];
      this.filterNumberOfReturnsRange = [0, 7];
      this.filterGPSTimeRange = [-Infinity, Infinity];
      this.filterPointSourceIDRange = [0, 65535];

      this.defaultRenderer = null;
      this.overlay = null;
      this.overlayCamera = null;

      this.controls = null;

      this.tilesManager = null;
      this.quantizedTerrainManager = null;

      this.skybox = null;
      this.background = null;
      this._customVisibilityConfig = null;

      this.initThree();

      {
        let canvas = this.renderer.domElement;
        canvas.addEventListener(
          'webglcontextlost',
          (e: any) => {
            console.log(e);
            (this as any).postMessage('WebGL context lost. \u2639');

            let gl = this.renderer.getContext();
            let error = gl.getError();
            console.log(error);
          },
          false,
        );
      }

      {
        this.overlay = new THREE.Scene();
        this.overlayCamera = new THREE.OrthographicCamera(0, 1, 1, 0, -1000, 1000);
      }

      this.pRenderer = new Renderer(this.renderer);

      {
        let near = 2.5;
        let far = 10.0;
        let fov = 90;

        this.shadowTestCam = new THREE.PerspectiveCamera(90, 1, near, far);
        this.shadowTestCam.position.set(3.5, -2.8, 8.561);
        this.shadowTestCam.lookAt(new THREE.Vector3(0, 0, 4.87));
      }

      let scene = new Scene();

      this.setScene(scene);

      {
        this.inputHandler = new InputHandler(this);
        this.inputHandler.setScene(this.scene);

        this.clippingTool = new ClippingTool(this);
        this.transformationTool = new TransformationTool(this);
        this.navigationCube = new NavigationCube(this);
        this.navigationCube.visible = false;

        this.createControls();

        this.clippingTool.setScene(this.scene);

        let onPointcloudAdded = (e: any) => {
          if (this.scene.pointclouds.length === 1) {
            let speed = e.pointcloud.boundingBox.getSize(new THREE.Vector3()).length();
            speed = speed / 5;
            this.baseMoveSpeed = speed;
            this.setMoveSpeed(speed);

            if (this.tilesManager) {
              this.tilesManager.setOriginFromPointCloud(e.pointcloud);
            }
            if (this.quantizedTerrainManager && !this.quantizedTerrainManager.enabled) {
              this.quantizedTerrainManager.setOriginFromPointCloud(e.pointcloud);
            }
          }
        };

        let onVolumeRemoved = (e: any) => {
          this.inputHandler.deselect(e.volume);
        };

        this.addEventListener('scene_changed', (e: any) => {
          this.inputHandler.setScene(e.scene);
          this.clippingTool.setScene(this.scene);

          if (!e.scene.hasEventListener('pointcloud_added', onPointcloudAdded)) {
            e.scene.addEventListener('pointcloud_added', onPointcloudAdded);
          }

          if (!e.scene.hasEventListener('volume_removed', onVolumeRemoved)) {
            e.scene.addEventListener('volume_removed', onVolumeRemoved);
          }
        });

        this.scene.addEventListener('volume_removed', onVolumeRemoved);
        this.scene.addEventListener('pointcloud_added', onPointcloudAdded);
      }

      {
        // set defaults
        this.setFOV(60);
        this.setEDLEnabled(false);
        this.setEDLRadius(1.4);
        this.setEDLStrength(0.4);
        this.setEDLOpacity(1.0);
        this.setClipTask(ClipTask.HIGHLIGHT);
        this.setClipMethod(ClipMethod.INSIDE_ANY);
        this.setPointBudget(1 * 1000 * 1000);
        this.setShowBoundingBox(false);
        this.setFreeze(false);
        this.setControls(this.orbitControls);
        this.setBackground('gradient');
      }

      // Initialize 3D Tiles Manager for Swiss terrain
      this.tilesManager = new TilesManager(this);

      // start rendering!
      this.renderer.setAnimationLoop(this.loop.bind(this));
      this.annotationTool = new AnnotationTool(this);
      this.measuringTool = new MeasuringTool(this);
      this.profileTool = new ProfileTool(this);
      this.volumeTool = new VolumeTool(this);
    } catch (e) {
      console.error('An error occurred during initialization:', e);
    }
  }

  // ------------------------------------------------------------------------------------
  // Viewer API
  // ------------------------------------------------------------------------------------

  setScene(scene: any): void {
    if (scene === this.scene) {
      return;
    }

    let oldScene = this.scene;
    this.scene = scene;

    this.dispatchEvent({
      type: 'scene_changed',
      oldScene: oldScene,
      scene: scene,
    });
  }

  setControls(controls: Controls): void {
    if (controls !== this.controls) {
      if (this.controls) {
        this.controls.enabled = false;
        this.inputHandler.removeInputListener(this.controls);
      }

      this.controls = controls;
      controls.enabled = true;
      this.inputHandler.addInputListener(controls);
    }
  }

  getControls(): Controls | null {
    return this.controls;
  }

  getMinNodeSize(): number {
    return this.minNodeSize;
  }

  setMinNodeSize(value: number): void {
    if (this.minNodeSize !== value) {
      this.minNodeSize = value;
      this.dispatchEvent({ type: 'minnodesize_changed', viewer: this });
    }
  }

  getBackground(): any {
    return this.background;
  }

  setBackground(bg: string): void {
    if (this.background === bg) {
      return;
    }

    if (bg === 'skybox') {
      const resourcePath = '/textures/skybox2/';
      this.skybox = Utils.loadSkybox(new URL(resourcePath, window.location.href).href);
    }

    this.background = bg;
    this.dispatchEvent({ type: 'background_changed', viewer: this });
  }

  setShowBoundingBox(value: boolean): void {
    if (this.showBoundingBox !== value) {
      this.showBoundingBox = value;
      this.dispatchEvent({ type: 'show_boundingbox_changed', viewer: this });
    }
  }

  getShowBoundingBox(): boolean {
    return this.showBoundingBox;
  }

  setMoveSpeed(value: number): void {
    if (this.moveSpeed !== value) {
      this.moveSpeed = value;
      this.dispatchEvent({ type: 'move_speed_changed', viewer: this, speed: value });
    }
  }

  getMoveSpeed(): number {
    return this.moveSpeed;
  }

  setWeightClassification(w: number): void {
    for (let i = 0; i < this.scene.pointclouds.length; i++) {
      this.scene.pointclouds[i].material.weightClassification = w;
      this.dispatchEvent({ type: 'attribute_weights_changed' + i, viewer: this });
    }
  }

  setFreeze(value: boolean): void {
    value = Boolean(value) as boolean;
    if (this.freeze !== value) {
      this.freeze = value;
      this.dispatchEvent({ type: 'freeze_changed', viewer: this });
    }
  }

  getFreeze(): boolean {
    return this.freeze;
  }

  getClipTask(): any {
    return this.clipTask;
  }

  getClipMethod(): any {
    return this.clipMethod;
  }

  setClipTask(value: any): void {
    if (this.clipTask !== value) {
      this.clipTask = value;

      this.dispatchEvent({
        type: 'cliptask_changed',
        viewer: this,
      });
    }
  }

  setClipMethod(value: any): void {
    if (this.clipMethod !== value) {
      this.clipMethod = value;

      this.dispatchEvent({
        type: 'clipmethod_changed',
        viewer: this,
      });
    }
  }

  setElevationGradientRepeat(value: any): void {
    if (this.elevationGradientRepeat !== value) {
      this.elevationGradientRepeat = value;

      this.dispatchEvent({
        type: 'elevation_gradient_repeat_changed',
        viewer: this,
      });
    }
  }

  setPointBudget(value: any): void {
    const newValue = parseInt(value);
    if (this._customVisibilityConfig && this._customVisibilityConfig.pointBudget !== newValue) {
      this._customVisibilityConfig.pointBudget = newValue;
      this.dispatchEvent({ type: 'point_budget_changed', viewer: this });
    }
  }

  getPointBudget(): number {
    return this._customVisibilityConfig ? this._customVisibilityConfig.pointBudget : 1000000;
  }

  getVisibilityConfig(): any {
    if (this._customVisibilityConfig) {
      return this._customVisibilityConfig;
    }

    if (!this._visibilityConfig) {
      this._visibilityConfig = new Map();
    }
    return {
      pointBudget: 1000000,
      maxNodesLoading: 4,
      transformVersions: this._visibilityConfig,
    };
  }

  setVisibilityConfig(config: any): void {
    this._customVisibilityConfig = config;
  }

  setDEMCollisionsEnabled(value: boolean): void {
    if (this.useDEMCollisions !== value) {
      this.useDEMCollisions = value;
      this.dispatchEvent({ type: 'use_demcollisions_changed', viewer: this });
    }
  }

  getDEMCollisionsEnabled(): boolean {
    return this.useDEMCollisions;
  }

  setEDLEnabled(value: boolean): void {
    value = (Boolean(value) && getFeatures()?.SHADER_EDL.isSupported()) as boolean;

    if (this.useEDL !== value) {
      this.useEDL = value;
      this.dispatchEvent({ type: 'use_edl_changed', viewer: this });
    }
  }

  getEDLEnabled(): boolean {
    return this.useEDL;
  }

  setEDLRadius(value: number): void {
    if (this.edlRadius !== value) {
      this.edlRadius = value;
      this.dispatchEvent({ type: 'edl_radius_changed', viewer: this });
    }
  }

  getEDLRadius(): number {
    return this.edlRadius;
  }

  setEDLStrength(value: number): void {
    if (this.edlStrength !== value) {
      this.edlStrength = value;
      this.dispatchEvent({ type: 'edl_strength_changed', viewer: this });
    }
  }

  getEDLStrength(): number {
    return this.edlStrength;
  }

  setEDLOpacity(value: number): void {
    if (this.edlOpacity !== value) {
      this.edlOpacity = value;
      this.dispatchEvent({ type: 'edl_opacity_changed', viewer: this });
    }
  }

  getEDLOpacity(): number {
    return this.edlOpacity;
  }

  setFOV(value: number): void {
    if (this.fov !== value) {
      this.fov = value;
      this.dispatchEvent({ type: 'fov_changed', viewer: this });
    }
  }

  getFOV(): number {
    return this.fov;
  }

  setClassifications(classifications: Record<number | 'DEFAULT', ClassificationEntry>): void {
    this.classifications = classifications;

    this.dispatchEvent({ type: 'classifications_changed', viewer: this });
  }

  setClassificationVisibility(key: string, value: boolean): void {
    const classifications = this.classifications as unknown as Record<string, ClassificationEntry>;
    if (!classifications[key]) {
      classifications[key] = { visible: value, name: 'no name', color: [0.5, 0.5, 0.5, 1.0] };
      this.dispatchEvent({ type: 'classification_visibility_changed', viewer: this });
    } else if (classifications[key].visible !== value) {
      classifications[key].visible = value;
      this.dispatchEvent({ type: 'classification_visibility_changed', viewer: this });
    }
  }

  toggleAllClassificationsVisibility(): void {
    const classifications = this.classifications as unknown as Record<string, ClassificationEntry>;
    let numVisible = 0;
    let numItems = 0;
    for (const key of Object.keys(classifications)) {
      if (classifications[key].visible) {
        numVisible++;
      }
      numItems++;
    }

    let visible = true;
    if (numVisible === numItems) {
      visible = false;
    }

    let somethingChanged = false;

    for (const key of Object.keys(classifications)) {
      if (classifications[key].visible !== visible) {
        classifications[key].visible = visible;
        somethingChanged = true;
      }
    }

    if (somethingChanged) {
      this.dispatchEvent({ type: 'classification_visibility_changed', viewer: this });
    }
  }

  zoomTo(node: ZoomableNode, factor: number, animationDuration = 0): void {
    let view = this.scene.view;

    let camera = this.scene.cameraP.clone();
    camera.rotation.copy(this.scene.cameraP.rotation);
    camera.rotation.order = 'ZXY';
    camera.rotation.x = Math.PI / 2 + view.pitch;
    camera.rotation.z = view.yaw;
    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.zoomTo(node, factor);

    let bs: THREE.Sphere;
    if (node.boundingSphere) {
      bs = node.boundingSphere;
    } else if (node.geometry && node.geometry.boundingSphere) {
      bs = node.geometry.boundingSphere;
    } else {
      bs = node.boundingBox!.getBoundingSphere(new THREE.Sphere());
    }
    bs = bs.clone().applyMatrix4(node.matrixWorld);

    let startPosition = view.position.clone();
    let endPosition = camera.position.clone();
    let startTarget = view.getPivot();
    let endTarget = bs.center;
    let startRadius = view.radius;
    let endRadius = endPosition.distanceTo(endTarget);

    let easing = TWEEN.Easing.Quartic.Out;

    {
      // animate camera position
      let pos = startPosition.clone();
      let tween = new TWEEN.Tween(pos, tweenGroup).to(endPosition, animationDuration);
      tween.easing(easing);

      tween.onUpdate(() => {
        view.position.copy(pos);
      });

      tween.start();
    }

    {
      // animate camera target
      let target = startTarget.clone();
      let tween = new TWEEN.Tween(target, tweenGroup).to(endTarget, animationDuration);
      tween.easing(easing);
      tween.onUpdate(() => {
        view.lookAt(target);
      });
      tween.onComplete(() => {
        view.lookAt(target);
        this.dispatchEvent({ type: 'focusing_finished', target: this });
      });

      this.dispatchEvent({ type: 'focusing_started', target: this });
      tween.start();
    }
  }

  getBoundingBox(pointclouds?: PointCloudOctree[]): THREE.Box3 {
    return this.scene.getBoundingBox(pointclouds);
  }

  getGpsTimeExtent(): number[] {
    const range = [Infinity, -Infinity];
    for (const pointcloud of this.scene.pointclouds) {
      const attributes = pointcloud.pcoGeometry.pointAttributes.attributes;
      const aGpsTime = attributes.find(a => a.name === 'gps-time');

      if (
        aGpsTime &&
        typeof aGpsTime.range[0] === 'number' &&
        typeof aGpsTime.range[1] === 'number'
      ) {
        range[0] = Math.min(range[0], aGpsTime.range[0]);
        range[1] = Math.max(range[1], aGpsTime.range[1]);
      }
    }

    return range;
  }

  fitToScreen(factor = 1, animationDuration = 0): void {
    let box = this.getBoundingBox(this.scene.pointclouds);

    let node = new THREE.Object3D() as unknown as ZoomableNode;
    node.boundingBox = box;

    this.zoomTo(node, factor, animationDuration);
    this.controls?.stop();
  }

  toggleNavigationCube(): void {
    this.navigationCube.visible = !this.navigationCube.visible;
  }

  setView(view: string): void {
    if (!view) return;

    switch (view) {
      case 'F':
        this.setFrontView();
        break;
      case 'B':
        this.setBackView();
        break;
      case 'L':
        this.setLeftView();
        break;
      case 'R':
        this.setRightView();
        break;
      case 'U':
        this.setTopView();
        break;
      case 'D':
        this.setBottomView();
        break;
    }
  }

  setTopView(): void {
    this.scene.view.yaw = 0;
    this.scene.view.pitch = -Math.PI / 2;

    this.fitToScreen();
  }

  setBottomView(): void {
    this.scene.view.yaw = -Math.PI;
    this.scene.view.pitch = Math.PI / 2;

    this.fitToScreen();
  }

  setFrontView(): void {
    this.scene.view.yaw = 0;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setBackView(): void {
    this.scene.view.yaw = Math.PI;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setLeftView(): void {
    this.scene.view.yaw = -Math.PI / 2;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setRightView(): void {
    this.scene.view.yaw = Math.PI / 2;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  flipYZ(): void {
    this.isFlipYZ = !this.isFlipYZ;
  }

  setCameraMode(mode: CameraMode): void {
    this.scene.cameraMode = mode;

    for (let pointcloud of this.scene.pointclouds) {
      pointcloud.material.useOrthographicCamera = mode == CameraMode.ORTHOGRAPHIC;
    }
  }

  getProjection(): string | null {
    const pointcloud = this.scene.pointclouds[0];

    if (pointcloud) {
      return pointcloud.projection;
    } else {
      return null;
    }
  }

  // ------------------------------------------------------------------------------------
  // Terrain API (3D Tiles and Quantized Mesh)
  // ------------------------------------------------------------------------------------

  async loadSwissTerrain(): Promise<void> {
    if (!this.tilesManager) {
      console.error('TilesManager not initialized');
      return;
    }
    return this.tilesManager.loadTiles();
  }

  async loadQuantizedMeshTerrain(textureLayer = 'map'): Promise<void> {
    if (!this.quantizedTerrainManager) {
      this.quantizedTerrainManager = new QuantizedMeshTerrainManager(this);

      if (this.scene.pointclouds.length > 0) {
        this.quantizedTerrainManager.setOriginFromPointCloud(this.scene.pointclouds[0]);
      }
    }
    this.quantizedTerrainManager.wmtsLayer =
      textureLayer === 'aerial'
        ? this.quantizedTerrainManager.wmtsLayerAerial
        : this.quantizedTerrainManager.wmtsLayerMap;
    return this.quantizedTerrainManager.loadTerrain();
  }

  setTerrainEnabled(enabled: boolean): void {
    if (this.tilesManager) {
      this.tilesManager.setEnabled(enabled);
    }
  }

  setQuantizedTerrainEnabled(enabled: boolean): void {
    if (this.quantizedTerrainManager) {
      this.quantizedTerrainManager.setEnabled(enabled);
    }
  }

  isTerrainEnabled(): boolean {
    return this.tilesManager ? this.tilesManager.isEnabled() : false;
  }

  isQuantizedTerrainEnabled(): boolean {
    return this.quantizedTerrainManager ? this.quantizedTerrainManager.isEnabled() : false;
  }

  toggleTerrain(): void {
    if (this.tilesManager) {
      this.tilesManager.setEnabled(!this.tilesManager.isEnabled());
    }
  }

  toggleQuantizedTerrain(): void {
    if (this.quantizedTerrainManager) {
      this.quantizedTerrainManager.setEnabled(!this.quantizedTerrainManager.isEnabled());
    }
  }

  // ------------------------------------------------------------------------------------
  // Viewer Internals
  // ------------------------------------------------------------------------------------

  createControls(): void {
    {
      // create FIRST PERSON CONTROLS
      this.fpControls = new FirstPersonControls(this);
      this.fpControls.enabled = false;
    }
    {
      // create ORBIT CONTROLS
      this.orbitControls = new OrbitControls(this);
      this.orbitControls.enabled = false;
    }
  }

  initThree(): void {
    let width = this.renderArea.clientWidth;
    let height = this.renderArea.clientHeight;

    let canvas = document.createElement('canvas');

    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      premultipliedAlpha: false,
      canvas: canvas,
      antialias: false,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    });
    this.renderer.sortObjects = false;
    this.renderer.setSize(width, height);
    this.renderer.autoClear = false;
    this.renderArea.appendChild(this.renderer.domElement);
    this.renderer.domElement.tabIndex = 2222;
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.addEventListener('mousedown', () => {
      this.renderer.domElement.focus();
    });

    // WebGL2 context - extensions are built-in
    let gl = this.renderer.getContext();
    // EXT_color_buffer_float may still need explicit enabling in some browsers
    gl.getExtension('EXT_color_buffer_float');
  }

  updateMaterialDefaults(pointcloud: any): void {
    const material = pointcloud.material;

    const attIntensity = pointcloud.getAttribute('intensity');

    if (attIntensity != null && material.intensityRange[0] === Infinity) {
      material.intensityRange = [...attIntensity.range];
    }
  }

  update(delta: number, timestamp: number): void {
    this.dispatchEvent({
      type: 'update_start',
      delta: delta,
      timestamp: timestamp,
    });

    const scene = this.scene;
    const camera = scene.getActiveCamera();
    if (!camera) {
      return;
    }
    const nearFarCamera = camera as THREE.Camera & { near: number; far: number };
    const visiblePointClouds = this.scene.pointclouds.filter(pc => pc.visible);

    const lTarget = camera.position
      .clone()
      .add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(1000));
    this.scene.directionalLight?.position.copy(camera.position);
    this.scene.directionalLight?.lookAt(lTarget);

    for (let pointcloud of visiblePointClouds) {
      pointcloud.showBoundingBox = this.showBoundingBox;
      pointcloud.generateDEM = this.generateDEM;
      pointcloud.minimumNodePixelSize = this.minNodeSize;

      let material = pointcloud.material;

      material.uniforms['uFilterReturnNumberRange'].value = this.filterReturnNumberRange;
      material.uniforms['uFilterNumberOfReturnsRange'].value = this.filterNumberOfReturnsRange;
      material.uniforms['uFilterGPSTimeClipRange'].value = this.filterGPSTimeRange;
      material.uniforms['uFilterPointSourceIDClipRange'].value = this.filterPointSourceIDRange;

      material.classification = this.classifications;
      material.recomputeClassification();

      this.updateMaterialDefaults(pointcloud);
    }

    {
      if (this.showBoundingBox) {
        let bbRoot = this.scene.scene.getObjectByName('potree_bounding_box_root');
        if (!bbRoot) {
          let node = new THREE.Object3D();
          node.name = 'potree_bounding_box_root';
          this.scene.scene.add(node);
          bbRoot = node;
        }

        let visibleBoxes: THREE.Object3D[] = [];
        for (let pointcloud of this.scene.pointclouds) {
          for (let node of pointcloud.visibleNodes.filter(vn => vn.boundingBoxNode !== undefined)) {
            let box = node.boundingBoxNode;
            if (box) visibleBoxes.push(box);
          }
        }

        bbRoot.children = visibleBoxes;
      }
    }

    if (!this.freeze) {
      const visConfig = this._customVisibilityConfig || this.getVisibilityConfig();
      let result = updatePointClouds(scene.pointclouds, camera, this.renderer, visConfig);

      if (result.lowestSpacing !== Infinity) {
        let near = result.lowestSpacing * 10.0;
        let far = -this.getBoundingBox().applyMatrix4(camera.matrixWorldInverse).min.z;

        far = Math.max(far * 1.5, 10000);
        near = Math.min(100.0, Math.max(0.01, near));
        far = Math.max(far, near + 10000);

        if (near === Infinity) {
          near = 0.1;
        }

        nearFarCamera.near = near;
        nearFarCamera.far = far;
      }

      if (this.scene.cameraMode == CameraMode.ORTHOGRAPHIC) {
        nearFarCamera.near = -nearFarCamera.far;
      }
    }

    this.scene.cameraP.fov = this.fov;

    let controls = this.getControls();
    if (controls !== null) {
      controls.setScene(scene);
      controls.update(delta);

      if (typeof (globalThis as Record<string, unknown>)['debugDisabled'] === 'undefined') {
        this.scene.cameraP.position.copy(scene.view.position);
        this.scene.cameraP.rotation.order = 'ZXY';
        this.scene.cameraP.rotation.x = Math.PI / 2 + this.scene.view.pitch;
        this.scene.cameraP.rotation.z = this.scene.view.yaw;
      }

      this.scene.cameraO.position.copy(scene.view.position);
      this.scene.cameraO.rotation.order = 'ZXY';
      this.scene.cameraO.rotation.x = Math.PI / 2 + this.scene.view.pitch;
      this.scene.cameraO.rotation.z = this.scene.view.yaw;
    }

    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

    {
      if (this._previousCamera === undefined) {
        this._previousCamera = scene.getActiveCamera()!.clone();
        this._previousCamera.rotation.copy(scene.getActiveCamera()!.rotation);
      }

      if (!this._previousCamera.matrixWorld.equals(camera.matrixWorld)) {
        this.dispatchEvent({
          type: 'camera_changed',
          previous: this._previousCamera,
          camera: camera,
        });
      } else if (!this._previousCamera.projectionMatrix.equals(camera.projectionMatrix)) {
        this.dispatchEvent({
          type: 'camera_changed',
          previous: this._previousCamera,
          camera: camera,
        });
      }

      this._previousCamera = scene.getActiveCamera()!.clone();
      this._previousCamera.rotation.copy(scene.getActiveCamera()!.rotation);
    }

    {
      // update clip boxes
      let boxes: THREE.Object3D[] = [];

      boxes.push(
        ...this.scene.volumes.filter((v): v is BoxVolume => v.clip && v instanceof BoxVolume),
      );

      // profile segments
      for (let profile of this.scene.profiles) {
        boxes.push(...profile.boxes);
      }

      // Needed for .getInverse(), pre-empt a determinant of 0
      let degenerate = (box: THREE.Object3D) => box.matrixWorld.determinant() !== 0;

      let clipBoxes: ClipBoxDescriptor[] = boxes.filter(degenerate).map(box => {
        box.updateMatrixWorld();

        let boxInverse = box.matrixWorld.clone().invert();
        let boxPosition = box.getWorldPosition(new THREE.Vector3());

        return { box: box, inverse: boxInverse, position: boxPosition };
      });

      let clipPolygons = this.scene.polygonClipVolumes.filter(
        vol => vol.initialized && (vol.enabled === undefined || vol.enabled),
      );

      // set clip volumes in material
      for (let pointcloud of visiblePointClouds) {
        pointcloud.material.setClipBoxes(clipBoxes);
        pointcloud.material.setClipPolygons(clipPolygons, this.clippingTool.maxPolygonVertices);
        pointcloud.material.clipTask = this.clipTask;
        pointcloud.material.clipMethod = this.clipMethod;
      }
    }

    {
      for (let pointcloud of visiblePointClouds) {
        pointcloud.material.elevationGradientRepat = this.elevationGradientRepeat;
      }
    }

    {
      // update navigation cube
      this.navigationCube.update(camera.rotation);
    }

    // Update 3D tiles if enabled
    if (this.tilesManager) {
      this.tilesManager.update();
    }

    // Update quantized mesh terrain if enabled
    if (this.quantizedTerrainManager) {
      this.quantizedTerrainManager.update();
    }

    tweenGroup.update(timestamp);

    this.dispatchEvent({
      type: 'update',
      delta: delta,
      timestamp: timestamp,
    });
  }

  getPRenderer(): any {
    if (this.useEDL && getFeatures()?.SHADER_EDL.isSupported()) {
      if (!this.edlRenderer) {
        this.edlRenderer = new EDLRenderer(this);
      }

      return this.edlRenderer;
    } else {
      if (!this.defaultRenderer) {
        this.defaultRenderer = new PotreeRenderer(this);
      }

      return this.defaultRenderer;
    }
  }

  getRawPRenderer(): any {
    return this.pRenderer;
  }

  renderDefault(): void {
    let pRenderer = this.getPRenderer();

    {
      // resize
      const width = this.scaleFactor * this.renderArea.clientWidth;
      const height = this.scaleFactor * this.renderArea.clientHeight;

      this.renderer.setSize(width, height);
      const pixelRatio = this.renderer.getPixelRatio();
      const aspect = width / height;

      const scene = this.scene;

      scene.cameraP.aspect = aspect;
      scene.cameraP.updateProjectionMatrix();

      let frustumScale = this.scene.view.radius;
      scene.cameraO.left = -frustumScale;
      scene.cameraO.right = frustumScale;
      scene.cameraO.top = (frustumScale * 1) / aspect;
      scene.cameraO.bottom = (-frustumScale * 1) / aspect;
      scene.cameraO.updateProjectionMatrix();

      scene.cameraScreenSpace.top = 1 / aspect;
      scene.cameraScreenSpace.bottom = -1 / aspect;
      scene.cameraScreenSpace.updateProjectionMatrix();
    }

    pRenderer.clear();

    pRenderer.render(this.renderer);
    if (this.overlay && this.overlayCamera) {
      this.renderer.render(this.overlay, this.overlayCamera);
    }
  }

  render(): void {
    try {
      this.renderDefault();
    } catch (e) {
      console.error('An error occurred during rendering:', e);
    }
  }

  loop(timestamp: number): void {
    this.update(this.clock.getDelta(), timestamp);
    this.render();
    incrementFrameNumber();
  }

  dispose(): void {
    this.renderer.setAnimationLoop(null);

    if (this.inputHandler) {
      this.inputHandler.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
    }

    if (this.tilesManager) {
      this.tilesManager.dispose?.();
    }

    if (this.quantizedTerrainManager) {
      this.quantizedTerrainManager.dispose?.();
    }
  }
}
